import Clickable from 'components/Common/Clickable'
import FormatCurrency from 'components/Common/FormatCurrency'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Label from 'LuxKit/Label/Label'
import LuxPlusLogo from 'luxPlus/components/Logos/LuxPlusLogo'
import LuxPlusBookingProtectionLabelLabel from 'luxPlus/components/LuxPlusBookingProtectionLabel'
import LuxPlusInsuranceLabel from 'luxPlus/components/LuxPlusInsuranceLabel'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import { FormattedMessage } from 'react-intl'
import LuxPlusMonthInfoModal from './LuxPlusMonthInfoModal'

const LOGO_HEIGHT = 9

interface Props extends Utils.PropsWithDataAttributes {
  type: App.LuxPlusLabelType
  saveAmount?: number
  hideSaveAmountPlus?: boolean
  currency?: string
  description?: string
}

function LuxPlusLabelMonth({
  currency,
  description,
  type,
  saveAmount,
  hideSaveAmountPlus,
  ...rest
}: Props) {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const showModal = useContext(ModalContext)
  const handleClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    event.stopPropagation()
    showModal(<LuxPlusMonthInfoModal />)
  }, [showModal])

  if (!luxPlusEnabled) return null

  const saveAmountSuffix = hideSaveAmountPlus ? '' : '+'

  return <Clickable onClick={handleClick} data-testid="lux-plus-month-label-clickable">
    <Label
      data-testid="lux-plus-month-label"
      {...rest}
      kind="primary"
      variant="lux-plus-month"
      endIcon={<LineInfoCircleIcon />}
    >
      {type === 'bonus-inclusions' && !description && <FormattedMessage
        id="94gZWo9CaOsmaindwVF0"
        defaultMessage="Bonus inclusions with {logo} Month"
        description="Form field label for bonus inclusions during LuxPlus+ Month"
        values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
      />}
      {type === 'bonus-inclusions' && !!description && <FormattedMessage
        id="hqLvOABhkfb-9JL2uyoZ"
        defaultMessage="{description} with {logo} Month"
        description="Label for bonus inclusions with custom description during LuxPlus+ Month"
        values={{
          description,
          logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
        }}
      />}
      {type === 'member' && <FormattedMessage
        id="xkG-hTDTOtaXIVdFTyj_"
        defaultMessage="{logo} member"
        description="Label indicating LuxPlus+ member status in the LuxPlus membership section"
        values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
      />}
      {type === 'only-logo' && <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />}
      {type === 'early-access-offer' && <FormattedMessage
        id="qPPzGfQ5fgNzNgwQnOeS"
        defaultMessage="Early access with {logo} Month"
        description="Label for early access offers during LuxPlus+ Month"
        values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
      />}
      {type === 'member-only-offer' && <FormattedMessage
        id="NEeM3cOIBZn_CtkAd8wK"
        defaultMessage="{logo} Month Member-only offer"
        description="Promotional label for member-only exclusive offers displayed during the LuxPlus+ Month campaign"
        values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
      />}
      {type === 'member-price-offer' && !!saveAmount && <FormattedMessage
        id="EQaqyfMPP0Bbi8bc2Ln2"
        defaultMessage="Save {amount}{suffix} with {logo} Month"
        description="Label showing member price savings during LuxPlus+ Month"
        values={{
          amount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
          suffix: saveAmountSuffix,
          logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
        }}
      />}
      {type === 'member-price-offer-lpp' && !!saveAmount && <FormattedMessage
        id="HJpHdCgiZmdEXY6Zugwp"
        defaultMessage="Save {amount}{suffix} with {logo}"
        description="Label showing member price savings for LuxPlus+ in the LuxPlus membership section"
        values={{
          amount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
          suffix: saveAmountSuffix,
          logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
        }}
      />}
      {type === 'member-price-offer-tour' && !!saveAmount && <FormattedMessage
        id="n46djxAKANssesbhoHwh"
        defaultMessage="Save {amount}{suffix}/person with {logo}"
        description="Label showing per-person member price savings for LuxPlus+ tours"
        values={{
          amount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
          suffix: saveAmountSuffix,
          logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
        }}
      />}
      {type === 'options-available-tour' && <FormattedMessage
        id="sU0DK9hhUoYIkzijRlsc"
        defaultMessage="{logo} options available"
        description="Label indicating LuxPlus+ options are available for tours"
        values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
      />}
      {type === 'plus' && <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} />}
      {type === 'plus-preview' && <FormattedMessage
        id="Cmos_iMfM1NM6MXkqWrT"
        defaultMessage="{logo} preview"
        description="Label for LuxPlus+ preview status in the guest reviews section"
        values={{ logo: <LuxPlusLogo colour="inherit" type="plus-only" height={LOGO_HEIGHT} /> }}
      />}
      {type === 'insurance' && !!saveAmount && <LuxPlusInsuranceLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
      {type === 'booking-protection' && !!saveAmount && <LuxPlusBookingProtectionLabelLabel saveAmount={saveAmount} logoHeight={LOGO_HEIGHT} />}
      {type === 'savings' && !!saveAmount && <FormattedMessage
        id="C-9rcf-DqgS4Iu3fIpF-"
        defaultMessage="Extra {amount} saved with {logo}"
        description="Label showing extra savings with LuxPlus+ membership"
        values={{
          amount: <FormatCurrency format="roundedDollar" value={saveAmount} />,
          logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} />,
        }}
      />}
      {type === 'member-inclusions' && <FormattedMessage
        id="En44tYyLvxtczKgweBC4"
        defaultMessage="Member inclusions with {logo}"
        description="Label for member inclusions with LuxPlus+ in the inclusions list"
        values={{ logo: <LuxPlusLogo colour="inherit" height={LOGO_HEIGHT} /> }}
      />}
    </Label>
  </Clickable>
}

export default LuxPlusLabelMonth
